import { c as _c } from "react/compiler-runtime";
import { Stack, Typography } from '@mui/material';
import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { RelativeTimeOption } from '@/data/inventory/relativeTime';
import { dayjs, type Dayjs } from '@/utils/dayjs';
import { DEFAULT_RELATIVE_TIME, defaultPresetConditions, presetConditions, readDateFilterRange, readSelectedProperty, replaceBound, retargetConditions, toRelativeValue } from './dateFilterState';
import { ALL_TIME, type DateFilterConfig, type TimeFilterValue } from './dateFilterTypes';
import { DateTimePropertySelector } from './dateTimePropertySelector';
import { FromToSelector } from './fromToSelector';
import { RelativeTimeSelector } from './relativeTimeSelector';

/**
 * The date filter's controls, driven entirely by props so a host can seat them
 * in its own layout and keep the conditions wherever it already keeps state.
 *
 * `conditions` holds only the conditions on the properties in `properties`, and
 * `onConditionsChange` replaces that whole set — the host merges the result back
 * into whatever else it holds.
 */
export const DateTimeFilterControls = t0 => {
  const $ = _c(71);
  const {
    properties,
    title,
    icon,
    fixedProperty: t1,
    relativeKey,
    onRelativeKeyChange,
    conditions,
    onConditionsChange
  } = t0;
  const fixedProperty = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      label: "from all time",
      value: ALL_TIME
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== properties) {
    t3 = [t2, ...properties];
    $[1] = properties;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const propertyOptions = t3;
  const noConditionProperty = fixedProperty ? properties[0]?.value ?? ALL_TIME : ALL_TIME;
  let t4;
  if ($[3] !== conditions) {
    t4 = readDateFilterRange(conditions);
    $[3] = conditions;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    from,
    to
  } = t4;
  let t5;
  if ($[5] !== conditions || $[6] !== noConditionProperty || $[7] !== properties) {
    t5 = readSelectedProperty(conditions, properties, noConditionProperty);
    $[5] = conditions;
    $[6] = noConditionProperty;
    $[7] = properties;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== from || $[10] !== t5 || $[11] !== to) {
    t6 = {
      selectedProperty: t5,
      selectedFrom: from,
      selectedTo: to
    };
    $[9] = from;
    $[10] = t5;
    $[11] = to;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    selectedProperty,
    selectedFrom,
    selectedTo
  } = t6;
  let t7;
  if ($[13] !== fixedProperty || $[14] !== relativeKey) {
    t7 = fixedProperty ? toRelativeValue(relativeKey) : (relativeKey || DEFAULT_RELATIVE_TIME) as TimeFilterValue;
    $[13] = fixedProperty;
    $[14] = relativeKey;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const relativeValue = t7;
  const isFiltering = fixedProperty ? relativeValue !== ALL_TIME : selectedProperty !== ALL_TIME;
  let t8;
  if ($[16] !== onConditionsChange || $[17] !== onRelativeKeyChange) {
    t8 = function clearFilter() {
      onConditionsChange([]);
      onRelativeKeyChange(undefined);
    };
    $[16] = onConditionsChange;
    $[17] = onRelativeKeyChange;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const clearFilter = t8;
  let t9;
  if ($[19] !== clearFilter || $[20] !== conditions || $[21] !== onConditionsChange || $[22] !== onRelativeKeyChange || $[23] !== selectedProperty) {
    t9 = function updateProperty(property) {
      if (property === ALL_TIME) {
        clearFilter();
        return;
      }
      if (selectedProperty === ALL_TIME) {
        onConditionsChange(defaultPresetConditions(property));
        onRelativeKeyChange(DEFAULT_RELATIVE_TIME);
        return;
      }
      onConditionsChange(retargetConditions(conditions, property));
    };
    $[19] = clearFilter;
    $[20] = conditions;
    $[21] = onConditionsChange;
    $[22] = onRelativeKeyChange;
    $[23] = selectedProperty;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const updateProperty = t9;
  let t10;
  if ($[25] !== clearFilter || $[26] !== onConditionsChange || $[27] !== onRelativeKeyChange || $[28] !== selectedFrom || $[29] !== selectedProperty || $[30] !== selectedTo) {
    t10 = function updateRelativeKey(nextKey) {
      if (nextKey === ALL_TIME) {
        clearFilter();
        return;
      }
      onConditionsChange(presetConditions(selectedProperty, nextKey, {
        from: selectedFrom,
        to: selectedTo
      }));
      onRelativeKeyChange(nextKey);
    };
    $[25] = clearFilter;
    $[26] = onConditionsChange;
    $[27] = onRelativeKeyChange;
    $[28] = selectedFrom;
    $[29] = selectedProperty;
    $[30] = selectedTo;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const updateRelativeKey = t10;
  let t11;
  if ($[32] !== conditions || $[33] !== onConditionsChange || $[34] !== selectedProperty) {
    t11 = function updateDateCondition(operator, value) {
      onConditionsChange(replaceBound(conditions, selectedProperty, operator, value));
    };
    $[32] = conditions;
    $[33] = onConditionsChange;
    $[34] = selectedProperty;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const updateDateCondition = t11;
  let t12;
  if ($[36] !== selectedFrom || $[37] !== updateDateCondition) {
    t12 = function updateFrom(from_0) {
      if (from_0.isSame(selectedFrom, "minute")) {
        return;
      }
      updateDateCondition(ConditionOperatorEnum.From, from_0);
    };
    $[36] = selectedFrom;
    $[37] = updateDateCondition;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const updateFrom = t12;
  let t13;
  if ($[39] !== selectedTo || $[40] !== updateDateCondition) {
    t13 = function updateTo(to_0) {
      if (to_0.isSame(selectedTo, "minute")) {
        return;
      }
      updateDateCondition(ConditionOperatorEnum.To, to_0);
    };
    $[39] = selectedTo;
    $[40] = updateDateCondition;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const updateTo = t13;
  let t14;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      gap: "12px",
      alignItems: "center"
    };
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      gap: "8px",
      alignItems: "center"
    };
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== title) {
    t16 = <Typography variant="subtitle1">{title}</Typography>;
    $[44] = title;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== fixedProperty || $[47] !== propertyOptions || $[48] !== selectedProperty || $[49] !== updateProperty) {
    t17 = !fixedProperty && <DateTimePropertySelector selectedValue={selectedProperty} onValueChange={updateProperty} options={propertyOptions} />;
    $[46] = fixedProperty;
    $[47] = propertyOptions;
    $[48] = selectedProperty;
    $[49] = updateProperty;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== fixedProperty || $[52] !== isFiltering || $[53] !== relativeValue || $[54] !== updateRelativeKey) {
    t18 = (fixedProperty || isFiltering) && <RelativeTimeSelector selectedRelativeKey={relativeValue} onRelativeKeyChange={updateRelativeKey} showAllTime={fixedProperty} stripInPrefix={fixedProperty} />;
    $[51] = fixedProperty;
    $[52] = isFiltering;
    $[53] = relativeValue;
    $[54] = updateRelativeKey;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== icon || $[57] !== t16 || $[58] !== t17 || $[59] !== t18) {
    t19 = <Stack direction="row" sx={t15}>{icon}{t16}{t17}{t18}</Stack>;
    $[56] = icon;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== isFiltering || $[62] !== relativeValue || $[63] !== selectedFrom || $[64] !== selectedTo || $[65] !== updateFrom || $[66] !== updateTo) {
    t20 = relativeValue === RelativeTimeOption.Between && isFiltering && <FromToSelector selectedFrom={selectedFrom ?? dayjs.utc().startOf("minute")} selectedTo={selectedTo ?? dayjs.utc().endOf("minute")} setSelectedFrom={updateFrom} setSelectedTo={updateTo} />;
    $[61] = isFiltering;
    $[62] = relativeValue;
    $[63] = selectedFrom;
    $[64] = selectedTo;
    $[65] = updateFrom;
    $[66] = updateTo;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== t19 || $[69] !== t20) {
    t21 = <Stack direction="row" sx={t14}>{t19}{t20}</Stack>;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  return t21;
};